import { useMutation } from '@tanstack/react-query';

import {
  REPORTING_LE_LENS_DATASET_DRY_RUN_PATH,
  reportingLeLensDatasetExtendCheckPath,
  reportingLeLensDatasetMergeCheckPath,
} from '~/consts/reportingLeLensApi';

import type { LensDatasetColumn } from '~/types/leLens';

import { encodeRequestBody } from '~/utils/encodeRequestBody';
import { requestWithTimeout } from '~/utils/requestWithTimeout';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

/**
 * The dataset dry-run and extend-check guard endpoints (00_PLAN.md, "Backend endpoints"; A2 and
 * A3's lanes, live-verified against the local backend). Both bill nothing extra beyond what they
 * say: a dry run is free, and an extend check runs the candidate SQL exactly once.
 */

export type LensDatasetDryRunResponse = {
  columns: Array<LensDatasetColumn>;
  bytesProcessed: number;
  /** Fully qualified (project.dataset.table) tables the dry run's job statistics reported this
   *  query reading. Empty for a query that touches no real table. */
  referencedTables: Array<string>;
};

const DRY_RUN_TIMEOUT_MESSAGE =
  'This query is taking longer than expected to check. Nothing was changed, so try again.';

/**
 * Types a candidate query and reports what it would scan, without running or billing for it. The
 * service runs its static checks first, so a script or an unreadable table comes back as a 400
 * naming the problem rather than a generic transport error.
 */
export function useDryRunReportingLeLensDataset() {
  return useMutation({
    mutationFn: async (sql: string) => {
      return await requestWithTimeout<LensDatasetDryRunResponse>(
        resolveReportingLeLensUrl(REPORTING_LE_LENS_DATASET_DRY_RUN_PATH),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(encodeRequestBody({ sql })),
        },
        DRY_RUN_TIMEOUT_MESSAGE,
      );
    },
    retry: false,
  });
}

export type LensDatasetColumnTypeChange = { name: string; from: string; to: string };

export type LensDatasetCardDiff = { cardId: string; before: unknown; after: unknown; identical: boolean };

export type LensDatasetExtendCheckResponse = {
  added: Array<string>;
  removed: Array<string>;
  typeChanges: Array<LensDatasetColumnTypeChange>;
  grainKept: boolean;
  cardDiffs: Array<LensDatasetCardDiff>;
  bytesProcessed: number;
};

export type ExtendCheckInput = {
  lensId: string;
  datasetId: string;
  sql: string;
  /** Grain keys to verify the rewrite against. Falls back server-side to the dataset's own
   *  declared grain when omitted. */
  grain?: Array<string>;
};

/**
 * Runs a candidate rewrite of an existing dataset once (billed) and shapes every card that reads
 * it against the old rows and the new ones, so a reader can see whether anything they already
 * have would change before Apply. Only the dashboard's owner may call this: it is a preview of a
 * structural change, gated the same way saving one is.
 */
export function useExtendCheckReportingLeLensDataset() {
  return useMutation({
    mutationFn: async ({ lensId, datasetId, sql, grain }: ExtendCheckInput) => {
      return await requestWithTimeout<LensDatasetExtendCheckResponse>(
        resolveReportingLeLensUrl(reportingLeLensDatasetExtendCheckPath(lensId, datasetId)),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(encodeRequestBody({ sql, ...(grain?.length ? { grain: { keys: grain } } : {}) })),
        },
        'This check is taking longer than expected. Nothing was changed, so try again.',
      );
    },
    retry: false,
  });
}

export type MergeCheckInput = {
  lensId: string;
  /** The dataset that would be absorbed. */
  from: string;
  /** The dataset that would survive. Its rows are unchanged without `sql`; with `sql`, it is the
   *  candidate query that would become its new SQL. */
  into: string;
  /** A candidate rewrite of `into`'s query, returning the union of both datasets' columns. Runs
   *  the union mode of the check below instead of the preview mode. */
  sql?: string;
};

/**
 * merge-check has two modes (checks.ts, `mergeCheck`). Without `sql` (preview mode): today's
 * plain column diff between `from` and `into`, `cardDiffs` for `from`'s cards only shaped
 * against `into`'s current rows, and `unionRequired`, the `from` columns `into` lacks. Non-empty
 * whenever the two datasets do not already share a full column set, which the frontend then asks
 * the model to close with an extend-only brief before a real merge is possible. With `sql`
 * (union mode, a candidate covering both datasets' columns): `columnsMissing` (empty when the
 * candidate is a true superset), `columnsAfter` (its typed columns), and `cardDiffs` for the
 * cards of both datasets, each shaped against its own current rows for "before" and the
 * candidate's new rows for "after".
 */
export type MergeCheckResult = {
  /** Preview mode only. */
  added?: Array<string>;
  removed?: Array<string>;
  typeChanges?: Array<LensDatasetColumnTypeChange>;
  /** Preview mode only: the `from` columns `into` is missing. Empty means no union step is
   *  needed at all. */
  unionRequired?: Array<string>;
  /** Union mode only: every column of `from` or `into` the candidate does not return, or returns
   *  under a different type. Empty means the candidate is a true superset. */
  columnsMissing?: Array<string>;
  /** Union mode only: the candidate's typed column list, the correct new `dataset.columns` for
   *  the merged survivor. */
  columnsAfter?: Array<LensDatasetColumn>;
  grainKept: boolean;
  cardDiffs: Array<LensDatasetCardDiff>;
  bytesProcessed: number;
};

/**
 * Previews, or with `sql`, runs folding `from` into `into` (F7). Same owner-only server gate as
 * extend-check (confirmed against checksController.ts).
 */
export function useMergeCheckReportingLeLensDataset() {
  return useMutation({
    mutationFn: async ({ from, into, lensId, sql }: MergeCheckInput) => {
      return await requestWithTimeout<MergeCheckResult>(
        resolveReportingLeLensUrl(reportingLeLensDatasetMergeCheckPath(lensId)),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(encodeRequestBody({ from, into, ...(sql !== undefined ? { sql } : {}) })),
        },
        'This check is taking longer than expected. Nothing was changed, so try again.',
      );
    },
    retry: false,
  });
}
